import { IsEmail, IsNotEmpty, ValidateIf } from 'class-validator';
import { z } from 'zod';

export interface ConsentLinkDto {
  id: number;
  key: string;
  userId: string;
  expiration: number;
  url: string;
}

export interface ConsentLinkCreationRequestDto {
  userId: string;
}

export interface ConsentLinkRefreshRequestDto {
  key: string;
}

export class TermResponseDto {
  constructor(data?: Partial<TermResponseDto>) {
    Object.assign(this, data);
  }

  @IsNotEmpty()
  termId: number;

  @IsNotEmpty({ message: 'A response is required' })
  consented: boolean;

  consentIdentityId?: string;
}

export class ConsentFormSubmissionDto {
  constructor(data?: Partial<ConsentFormSubmissionDto>) {
    Object.assign(this, data);
  }

  consentLinkKey?: string;

  consentIdentityId?: string;

  @IsNotEmpty()
  termId: number;

  @IsNotEmpty({ message: 'A response is required' })
  consented: boolean;
}

export class IdentitySubmissionDto {
  constructor(data?: Partial<IdentitySubmissionDto>) {
    Object.assign(this, data);
  }
  consentLinkKey: string;
  @IsNotEmpty({ message: 'Name is required' })
  name: string;
  @IsNotEmpty({ message: 'Date of birth is required' })
  dateOfBirth: string;
  @ValidateIf(identity => identity.email)
  @IsEmail({}, { message: 'Email is not valid' })
  email: string;
  line1: string;
  city: string;
  state: string;
  zip: string;
}

export interface TermIdentityDto {
  id: string;
  createdAt: number;
}
export interface TermResponseSubmissionDto {
  id: number;
  userId: string;
  consentLinkId: number;
  internalSubmitterUserId: string;
  createdAt: number;
  termIdentityId: number;
  ipAddress: string;
  languageCode: string;
}
export interface TermDto {
  id: number;
  key: string;
  name: string;
  shortName: string;
  description: string;
  infoText: string;
  text: string;
  acceptText: string;
  rejectText: string;
  internalNonConsentLimitation: string;
  createdAt?: number;
  identityRequired: boolean;
  consented?: boolean;
  termResponseSubmission?: TermResponseSubmissionDto;
  history?: TermResponseDto[];
  clientConfiguration: JSON;
}

export interface TermContentDto {
  id: number;
  key: string;
  content: string;
  infoContent?: string;
}

export interface TermGroupDto {
  id: number;
  clientId: number;
  key: string;
  name: string;
  userHeader: string;
  userOverview: string;
  terms: TermDto[];
  createdAt: number;
}

export interface ConsentGroupsDto {
  termGroups: TermGroupDto[];
}

export interface ConsentGoalDto {
  id: number;
  key: string;
  name: string;
  description: string;
  goalType: string;
  consented: boolean;
  completed: boolean;
  completedAt: number;
  memberId: string;
  term: TermDto;
}

export const ConsentIdentitySchema = z
  .object({
    name: z.string().min(2, { message: 'validation.required' }),
    dateOfBirth: z.string().min(8, { message: 'validation.required' }),
    email: z.string().email().optional(),
    line1: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    consentLinkKey: z.string().optional(),
  })
  .strict();

export const ConsentFormSchema = z
  .object({
    consentLinkKey: z.string().optional(),
    consentIdentityId: z.string().optional(),
    termId: z.number(),
    consented: z.coerce.boolean(),
  })
  .strict();
